/* eslint-disable jsx-a11y/no-redundant-roles -- role='list' / 'listitem' are the design's: Safari drops list semantics from a list styled with list-style: none */
import classNames from 'classnames';
import { Trans, useTranslation } from 'next-i18next';
import type { CSSProperties } from 'react';

import { Picture } from '../components/picture';
import type { SignupFormProps } from '../components/signup-form';
import { SignupForm } from '../components/signup-form';
import { CONTAINER_CLASS, EYEBROW_CLASS, SECTION_TITLE_CLASS } from '../components/ui';
import { FINAL_FORM_ANCHOR, V4A_NAMESPACE } from '../constants';

interface Spark {
  x: string;
  y: string;
  delay: string;
  scale: string;
  side: 'start' | 'end';
}

const SPARKS: readonly Spark[] = [
  { x: '.3', y: '26%', delay: '.4s', scale: '1', side: 'start' },
  { x: '.66', y: '70%', delay: '2.2s', scale: '.7', side: 'start' },
  { x: '.28', y: '18%', delay: '1.2s', scale: '1.1', side: 'end' },
  { x: '.6', y: '62%', delay: '3s', scale: '.8', side: 'end' },
];

const SPARK_CLASS = 'spark spark--close text-[color:var(--gold)] md:absolute md:top-[var(--sy)] md:h-[6px] md:w-[6px] md:rounded-[50%] md:bg-[color:var(--gold-hi)] md:[box-shadow:0_0_11px_2px_rgb(var(--gold-rgb)_/_.7)]';
const RAIL_ITEM_CLASS = 'grid min-w-0 gap-[.15rem] border-s border-solid border-s-[color:rgb(var(--gold-rgb)_/_.3)] ps-3';
const RAIL_FIGURE_CLASS = 'tabular whitespace-nowrap text-[length:clamp(1.25rem,_1rem_+_.9vw,_1.75rem)] font-bold tabular-nums tracking-[-.01em] text-[color:var(--gold)]';
const RAIL_LABEL_CLASS = 'font-mono text-[length:var(--fs-2xs)] uppercase tracking-[var(--ls-eyebrow)] text-[color:var(--fg-faint-aa)]';
const CNTBOX_CLASS = 'cntbox inline-block whitespace-nowrap text-start normal-case tracking-[inherit] text-inherit';
const CNT_CLASS = 'cnt normal-case tracking-[inherit] text-inherit';

function Tick() {
  return (
    <span className='tick inline-flex size-[1.55rem] items-center justify-center rounded-[50%] border border-solid border-[color:rgb(var(--accent-rgb)_/_.38)] bg-[color:rgb(var(--accent-rgb)_/_.12)] text-[color:var(--accent)] forced-colors:border-[color:CanvasText]' aria-hidden='true'>
      <svg viewBox='0 0 24 24' width='15' height='15' fill='none' stroke='currentColor' strokeWidth='2.4' strokeLinecap='round' strokeLinejoin='round' focusable='false'>
        <path d='m5 12.5 4.5 4.5L19 7' />
      </svg>
    </span>
  );
}

type FinalCtaProps = Omit<SignupFormProps, 'idPrefix'>;

/** "Quebre a maldição antes da próxima ranqueada": the closing band with the second enrolment card. */
export function FinalCta(formProps: FinalCtaProps) {
  const { t } = useTranslation(V4A_NAMESPACE);
  return (
    <section className='sec sec--final relative isolate py-[clamp(4rem,_2rem_+_7vw,_8rem)]' id='comecar' aria-labelledby='final-title'>
      <div className='sec__bg pointer-events-none absolute inset-0 z-[-1] overflow-hidden' aria-hidden='true'>
        <span className='sec__halo sec__halo--close absolute top-[42%] aspect-[1] w-[min(96rem,_138%)] rounded-[50%]' />
        <span className='sec__weave absolute -inset-px' />
        <svg className='final__sigil' viewBox='0 0 560 560' width='560' height='560' focusable='false'>
          <g fill='none' stroke='currentColor' strokeLinecap='round'>
            <circle cx='280' cy='280' r='272' strokeWidth='1' opacity='.45' />
            <circle cx='280' cy='280' r='242' strokeWidth='1' strokeDasharray='2 15' opacity='.65' />
            <circle cx='280' cy='280' r='188' strokeWidth='1' opacity='.3' />
            <circle cx='280' cy='280' r='118' strokeWidth='1' strokeDasharray='3 11' opacity='.4' />
            <path d='M280 92 443 374H117Z' strokeWidth='1' opacity='.4' />
            <path d='M280 468 117 186h326Z' strokeWidth='1' opacity='.24' />
            <g strokeWidth='1.6' opacity='.75'>
              <path d='M280 8v24M280 528v24M8 280h24M528 280h24' />
              <path d='M92 92l18 18M468 92l-18 18M92 468l18-18M468 468l-18-18' />
            </g>
          </g>
        </svg>
        {SPARKS.map((spark) => (
          <span key={`${spark.side}-${spark.x}`} className={classNames(SPARK_CLASS, spark.side === 'start' ? 'md:start-[calc(var(--sx)_*_max(0px,_calc(var(--band)_-_16px)))]' : 'spark--r md:end-[calc(var(--sx)_*_max(0px,_calc(var(--band)_-_16px)))] md:start-auto')} style={{ '--sx': spark.x, '--sy': spark.y, '--sd': spark.delay, '--ss': spark.scale } as CSSProperties} />
        ))}
      </div>

      <div className={`${CONTAINER_CLASS} final__inner grid items-start gap-[clamp(2rem,_1rem_+_3vw,_3.5rem)] min-[992px]:grid-cols-[minmax(0,_1fr)_minmax(0,_var(--form-max))] min-[992px]:items-center`}>
        <div className='final__copy min-w-0' data-reveal=''>
          <p className={EYEBROW_CLASS}>{t('final.eyebrow')}</p>
          <h2 className={`${SECTION_TITLE_CLASS} final__title max-w-[15em]`} id='final-title'>
            <Trans t={t} i18nKey='final.title' components={{ em: <em className='not-italic text-[color:var(--gold)] [text-shadow:0_0_30px_rgb(var(--gold-rgb)_/_.4)]' /> }} />
          </h2>
          <p className='final__lead m-0 mt-5 max-w-[32em] text-[length:var(--fs-lg)] leading-[1.6] text-[color:var(--fg-muted)]'>{t('final.lead')}</p>
          <ul className='final__list m-0 mt-[clamp(1.5rem,_1rem_+_1.6vw,_2.25rem)] grid list-none gap-3 p-0' role='list'>
            {(['access', 'no_card', 'safe'] as const).map((key) => (
              <li key={key} className='grid grid-cols-[auto_minmax(0,_1fr)] items-start gap-3 text-[length:var(--fs-md)] leading-[1.55] text-[color:var(--fg-muted)]'>
                <Tick />
                {t(`final.list.${key}`)}
              </li>
            ))}
          </ul>
          <ul className='final__rail m-0 mt-[clamp(1.75rem,_1rem_+_2vw,_2.5rem)] grid list-none grid-cols-2 gap-4 border-t border-solid border-t-[color:var(--rim)] p-0' role='list' data-count-group=''>
            <li className={RAIL_ITEM_CLASS}>
              <b className={RAIL_FIGURE_CLASS}>
                {t('rail.up_to')}{' '}
                <span className={CNTBOX_CLASS} aria-hidden='true'>
                  <span className={CNT_CLASS} data-count-to='80'>
                    80
                  </span>
                  %
                </span>
                <span className='sr-only'>80%</span>
              </b>
              <span className={RAIL_LABEL_CLASS}>{t('rail.less_ping')}</span>
            </li>
            <li className={RAIL_ITEM_CLASS}>
              <b className={RAIL_FIGURE_CLASS}>
                {t('rail.up_to')}{' '}
                <span className={CNTBOX_CLASS} aria-hidden='true'>
                  <span className={CNT_CLASS} data-count-to='110'>
                    110
                  </span>
                  %
                </span>
                <span className='sr-only'>110%</span>
              </b>
              <span className={RAIL_LABEL_CLASS}>{t('rail.more_fps')}</span>
            </li>
            <li className={RAIL_ITEM_CLASS}>
              <b className={RAIL_FIGURE_CLASS}>
                <span className={CNTBOX_CLASS} aria-hidden='true'>
                  <span className={CNT_CLASS} data-count-to='2000'>
                    {t('map.stats.servers.figure')}
                  </span>
                  +
                </span>
                <span className='sr-only'>{t('map.stats.servers.figure')}+</span>
              </b>
              <span className={RAIL_LABEL_CLASS}>{t('rail.servers')}</span>
            </li>
            <li className={RAIL_ITEM_CLASS}>
              <b className={RAIL_FIGURE_CLASS}>
                <span className={CNTBOX_CLASS} aria-hidden='true'>
                  <span className={CNT_CLASS} data-count-to='3000000'>
                    {t('map.stats.players.figure')}
                  </span>
                  +
                </span>
                <span className='sr-only'>{t('map.stats.players.figure')}+</span>
              </b>
              <span className={RAIL_LABEL_CLASS}>{t('rail.players')}</span>
            </li>
          </ul>
          <p className='final__certs m-0 mt-[clamp(1.25rem,_.9rem_+_1.2vw,_1.75rem)] flex flex-wrap items-center gap-x-4 gap-y-3 border-t border-solid border-t-[color:var(--rim-2)] pt-4'>
            <span className={RAIL_LABEL_CLASS}>{t('trust.certified_by')}</span>
            <Picture name='cert-microsoft-96' alt='Microsoft' width={96} height={99} loading='lazy' className='h-[2.3rem] w-auto' />
            <Picture name='cert-digicert-128' alt='DigiCert' width={128} height={52} loading='lazy' className='is-digicert h-[1.6rem] w-auto' />
          </p>
        </div>

        <div className='final__form mx-auto grid w-full min-w-0 max-w-[var(--form-max)] justify-items-start' id={FINAL_FORM_ANCHOR} data-reveal=''>
          <p className='formk m-0 mb-3 inline-flex items-center gap-2 rounded-full border border-solid border-[color:rgb(var(--gold-rgb)_/_.32)] bg-[color:rgb(var(--gold-rgb)_/_.09)] px-[.7rem] py-[.4rem] font-mono text-[length:var(--fs-2xs)] font-medium uppercase tracking-[var(--ls-eyebrow)] text-[color:var(--gold)]'>
            <i className='size-[7px] rounded-none bg-[color:var(--gold)] [box-shadow:0_0_10px_rgb(var(--gold-rgb)_/_.8)]' aria-hidden='true' />
            {t('form.kicker')}
          </p>
          <SignupForm idPrefix='nf' {...formProps} />
        </div>
      </div>
    </section>
  );
}
